import {
  AiBlockType,
  ComparisonValueType,
  ConditionComparator,
  ConditionLogic,
  IntegrationAuthApp,
} from '@prisma/client';
import { v4 as UUID } from 'uuid';

import { DYNAMIC_EDITOR_TAG_EXTENSION_NAME } from '@/components/dynamic-input/constants';
import { URL_ID } from '@/components/flow-canvas/components/side-menu/network-menu/http-api-menu.core';
import { gmailOperationsDefinition } from '@/levity/integrations/registry/email/providers/gmail.definition';
import { extractOperationsDefinition } from '@/levity/integrations/registry/llms/extract.definition';
import { generateOperationsDefinition } from '@/levity/integrations/registry/llms/generate.definition';
import { networkOperationsDefinition } from '@/levity/integrations/registry/network/network.definition';

import { AppNodeType, DynamicTagType, FlowNodeType, type FlowTemplate } from '../../types';

const IDs = {
  aiBlocks: {
    classifier: UUID(),
    extractor: UUID(),
    generator: UUID(),
  },
  nodes: {
    gmailTrigger: UUID(),
    classifier: UUID(),
    extractor: UUID(),
    generator: UUID(),
    pathTrackTrace: UUID(),
    pathOther: UUID(),
    networkRequest: UUID(),
    gmailDraft: UUID(),
  },
  metadata: {
    trackTrace: UUID(),
    other: UUID(),
    generateContext: UUID(),
    generateSentence: UUID(),
    iata: UUID(),
  },
};

const GMAIL_ICON = {
  alt: 'gmailV1 application icon',
  src: '/imgs/icons/gmailV1.svg',
};

export const airFreightTrackTraceTemplate: FlowTemplate = {
  name: 'air-freight-track-trace',
  description: 'Template for tracking and tracing air freight using email and API integrations.',
  integrations: [IntegrationAuthApp.gmailV1, IntegrationAuthApp.network],
  title: 'Reply to track & trace requests automatically',
  nodes: [
    {
      id: IDs.nodes.gmailTrigger,
      type: FlowNodeType.APP,
      icon: `/imgs/icons/gmailV1.svg`,
      metadata: {
        type: AppNodeType.TRIGGER,
        integration: IntegrationAuthApp.gmailV1,
        operationId: gmailOperationsDefinition.GetAllMessages.id,
      },
    },
    {
      id: IDs.nodes.classifier,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/classify.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.classifier,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Subject',
              image: GMAIL_ICON,
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Body',
              image: GMAIL_ICON,
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.extractor,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/extract.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.extractor,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Subject',
              image: {
                alt: 'gmailV1 application icon',
                src: '/imgs/icons/gmailV1.svg',
              },
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Body',
              image: {
                alt: 'gmailV1 application icon',
                src: '/imgs/icons/gmailV1.svg',
              },
            },
          },
        },
      ],
      dynamicOutputKeys: [
        {
          name: 'IATA flight number',
          code: 'iATAFlightNumber',
          default: false,
          rawName: 'IATA flight number',
          provider: 'aiblocks/extract',
        },
      ],
    },
    {
      id: IDs.nodes.generator,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/generate.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.generator,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Subject',
              image: {
                alt: 'http application icon',
                src: '/imgs/icons/gmailV1.svg',
              },
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: gmailOperationsDefinition.GetAllMessages.id,
              key: 'Body',
              image: {
                alt: 'gmailV1 application icon',
                src: '/imgs/icons/gmailV1.svg',
              },
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: networkOperationsDefinition.ExecuteHttpRequest.id,
              key: 'httpResponse',
              image: {
                alt: 'gmailV1 application icon',
                src: '/imgs/icons/network.svg',
              },
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.pathTrackTrace,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'track & trace',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
    {
      id: IDs.nodes.pathOther,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'other',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
    {
      id: IDs.nodes.networkRequest,
      type: FlowNodeType.APP,
      icon: `/imgs/icons/network.svg`,
      metadata: {
        type: AppNodeType.TARGET,
        integration: IntegrationAuthApp.network,
        operationId: networkOperationsDefinition.ExecuteHttpRequest.id,
        requestType: 'get',
      },
      connectedNodeInputId: URL_ID,
      defaultDynamicInputContent: [
        {
          type: DynamicTagType.TEXT,
          text: 'https://api.aviationstack.com/v1/flights?access_key={your-key-here}&flight_iata=',
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: extractOperationsDefinition.ExtractText.id,
              key: 'iATAFlightNumber',
              image: {
                alt: 'aiblocks/extract application icon',
                src: '/imgs/icons/aiblocks/extract.svg',
              },
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.gmailDraft,
      type: FlowNodeType.APP,
      icon: `/imgs/icons/gmailV1.svg`,
      metadata: {
        type: AppNodeType.TARGET,
        integration: 'gmailV1',
        operationId: 'gmail-create-draft',
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: generateOperationsDefinition.GenerateText.id,
              key: 'Generation Result',
              image: {
                alt: 'aiBlock application icon',
                src: '/imgs/icons/aiblocks/generate.svg',
              },
            },
          },
        },
      ],
    },
  ],
  edges: [
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.gmailTrigger,
      targetId: IDs.nodes.classifier,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.classifier,
      targetId: IDs.nodes.pathTrackTrace,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.classifier,
      targetId: IDs.nodes.pathOther,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.pathTrackTrace,
      targetId: IDs.nodes.extractor,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.extractor,
      targetId: IDs.nodes.networkRequest,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.networkRequest,
      targetId: IDs.nodes.generator,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.generator,
      targetId: IDs.nodes.gmailDraft,
    },
  ],
  aiBlocks: [
    {
      id: IDs.aiBlocks.classifier,
      name: 'Classifier',
      emoji: '🍉',
      type: 'classify',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: 'track & trace',
          id: UUID(),
          value: 'track & trace',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'other',
          id: UUID(),
          value: 'other',
          type: 'AiBlockLabel',
        },
      ],
    },
    {
      id: IDs.aiBlocks.extractor,
      name: 'Extractor',
      emoji: '🍉',
      type: 'extract',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: 'IATA flight number',
          id: UUID(),
          value: 'IATA flight number',
          type: 'AiBlockLabel',
        },
      ],
    },
    {
      id: IDs.aiBlocks.generator,
      name: 'Generator',
      emoji: '🍉',
      type: 'generate',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: '',
          id: UUID(),
          value:
            'Based on the original track and trace request and the context from the API. Craft a reply to the customer.',
          type: 'Context',
        },
        {
          labelSource: 'UserAnnotated',
          key: '',
          id: UUID(),
          value: '4',
          type: 'SentenceLength',
        },
      ],
    },
  ],
};
